@file:Suppress("MagicNumber", "TooGenericExceptionCaught")

package com.nonopn.skydome.common.app.pages.main.model

import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.material3.DrawerState
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.dokar.sonner.ToastType
import com.nonopn.geolocation.GpsLocation
import com.nonopn.geolocation.GpsResult
import com.nonopn.skydome.common.app.api.GeocodeLatLng
import com.nonopn.skydome.common.app.api.GeocodeResult
import com.nonopn.skydome.common.app.api.impl.ApiInterface
import com.nonopn.skydome.common.app.home.AppModel
import com.nonopn.skydome.common.app.pages.estimate.DefaultZoom
import com.nonopn.skydome.common.app.pages.estimate.MaxZoom
import com.nonopn.skydome.common.app.pages.estimate.MinZoom
import com.nonopn.skydome.common.app.pages.main.model.api.AeroguardLocation
import com.nonopn.skydome.common.app.pages.main.model.api.Probe
import com.nonopn.skydome.common.app.pages.main.model.api.Project
import com.nonopn.skydome.common.app.pages.main.model.api.ThunderDetectionActivity
import com.nonopn.skydome.common.app.pages.main.view.map.ShowThunderActivityInfo
import com.nonopn.skywatcher.app.file.saveFile
import com.nonopn.skywatcher.app.utils.Clock
import com.nonopn.skywatcher.app.utils.safeLaunch
import com.nonopn.skywatcher.common.api.ApiSession
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.dismiss
import com.nonopn.skywatcher.common.app.res.error_message_title
import com.nonopn.skywatcher.design.theme.AppColor
import com.nonopn.skywatcher.design.widgets.endOfDay
import com.nonopn.skywatcher.design.widgets.startOfDay
import com.nonopn.skywatcher.maps.LatLng
import com.nonopn.skywatcher.maps.MapUtils
import com.nonopn.skywatcher.maps.XY
import com.nonopn.skywatcher.maps.providers.GoogleProvider
import com.nonopn.skywatcher.maps.testMahalanobisKMP
import eu.codlab.sentry.wrapper.Sentry
import eu.codlab.viewmodel.StateHandler
import eu.codlab.viewmodel.StateViewModel
import eu.codlab.viewmodel.launch
import korlibs.io.async.CIO
import korlibs.time.DateTime
import korlibs.time.DateTimeTz
import korlibs.time.hours
import korlibs.time.minutes
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.async
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.withContext
import kotlinx.serialization.Serializable
import org.jetbrains.compose.resources.getString
import ovh.plrapps.mapcompose.api.ExperimentalClusteringApi
import ovh.plrapps.mapcompose.api.addCallout
import ovh.plrapps.mapcompose.api.addClusterer
import ovh.plrapps.mapcompose.api.addLayer
import ovh.plrapps.mapcompose.api.addMarker
import ovh.plrapps.mapcompose.api.addPath
import ovh.plrapps.mapcompose.api.centroidSnapshotFlow
import ovh.plrapps.mapcompose.api.enableFlingZoom
import ovh.plrapps.mapcompose.api.enableRotation
import ovh.plrapps.mapcompose.api.enableScrolling
import ovh.plrapps.mapcompose.api.enableZooming
import ovh.plrapps.mapcompose.api.maxScale
import ovh.plrapps.mapcompose.api.onLongPress
import ovh.plrapps.mapcompose.api.referentialSnapshotFlow
import ovh.plrapps.mapcompose.api.removeMarker
import ovh.plrapps.mapcompose.api.removePath
import ovh.plrapps.mapcompose.api.scale
import ovh.plrapps.mapcompose.api.setMagnifyingFactor
import ovh.plrapps.mapcompose.api.snapScrollTo
import ovh.plrapps.mapcompose.api.visibleBoundingBox
import ovh.plrapps.mapcompose.core.TileStreamProvider
import ovh.plrapps.mapcompose.ui.state.MapState
import ovh.plrapps.mapcompose.ui.state.OnActionListener
import ovh.plrapps.mapcompose.ui.state.markers.model.RenderingStrategy
import kotlin.math.max
import kotlin.math.min
import kotlin.math.pow
import kotlin.time.Duration.Companion.seconds
import kotlin.time.Duration.Companion.minutes as kotlinMinutes

var sessionLookUpId = 0

@Serializable
data class MapActionLookUpState(
    val id: Int = sessionLookUpId,
    val lookingUp: Boolean = false,
    val address: String = "",
    val results: List<GeocodeResult> = emptyList()
)

data class MainScreenModelState(
    val isAdmin: Boolean = false,
    val initialized: Boolean = false,
    val currentCoordinates: LatLng? = null,
    val currentZoom: Double? = null,
    val updatingCoordinates: Boolean = false,
    val apiSession: ApiSession? = null,
    val loading: Boolean = false,
    val probes: List<Probe> = emptyList(),
    val projects: List<Project> = emptyList(),
    val lightningActivity: List<ThunderDetectionActivity> = emptyList(),
    val visibleLightningActivity: Int = 0,
    val path: List<LatLng> = emptyList(),
    val selectedDate: DateTimeTz,
    val selectedDateFullDay: Boolean = false,
    val selectedDateRangeStart: DateTimeTz? = null,
    val selectedProject: Project? = null,
    val selectedPoint: LatLng? = null,
    val aeroguardLocations: List<AeroguardLocation>? = null,
    val aeroguardLocationsShow: Boolean = false,
    val startGroupingCircles: Boolean = false,
    val selectedProbes: List<Probe> = emptyList(),
    val showSelectPointHint: Boolean = false,
)

interface IMapAction {
    fun zoomIn()

    fun zoomOut()

    fun moveToOrigin()

    val lookUpState: StateFlow<MapActionLookUpState>

    fun lookUp(address: String)

    fun moveTo(location: GeocodeLatLng)
}

interface IMapGroupProbes {
    fun stopGroupingProbes()
}

interface MainScreenModel : IMapAction, StateHandler<MainScreenModelState>, IMapGroupProbes {
    fun configureMapState()

    fun closeLightningDrawer()

    fun openLightningDrawer()

    fun setFilterAeroguard(show: Boolean)

    fun setSelectedDate(date: DateTimeTz)

    fun setSelectedDateRange(from: DateTimeTz, to: DateTimeTz)

    fun setSelectedFullDay(fullDay: Boolean)

    fun previousSelectedDate()

    fun nextSelectedDate()

    fun previousSelectedDateDay()

    fun nextSelectedDateDay()

    val levelCount: Int
    val fullWidthOrHeight: Int
    val mapState: MapState

    fun saveKeraunicReport(
        location: LatLng,
        from: DateTimeTz,
        to: DateTimeTz,
        project: Project? = null,
        onDone: () -> Unit
    )

    var lightningDrawerState: DrawerState?
    var scope: CoroutineScope?

    fun updateCurrentInterface(newInterface: ApiInterface) = Unit
}

private fun defaultDetaForSelection(): DateTimeTz {
    var endOfInterval = DateTime.now().local.endOfDay
    val now = DateTime.now().local
    if (now > endOfInterval) return endOfInterval
    while (now < endOfInterval) {
        endOfInterval -= 30.minutes
    }

    return endOfInterval
}

@OptIn(FlowPreview::class)
@Suppress("LargeClass", "TooManyFunctions")
class MainScreenModelImpl(
    private val appModel: AppModel,
    private var currentInterface: ApiInterface,
    isAdmin: Boolean = false
) : StateViewModel<MainScreenModelState>(
    MainScreenModelState(
        isAdmin = isAdmin,
        path = emptyList(),
        selectedDateFullDay = false,
        selectedDate = defaultDetaForSelection()
    )
),
    MainScreenModel {
    private val _lookUpState = MutableStateFlow(MapActionLookUpState())
    override val lookUpState = _lookUpState.asStateFlow()

    private val clock = Clock(viewModelScope)
    private val googleApi = GoogleProvider()

    private val tileStreamProvider = TileStreamProvider { row, col, zoomLvl ->
        googleApi.getTile(row, col, zoomLvl)
    }

    override fun updateCurrentInterface(newInterface: ApiInterface) {
        currentInterface = newInterface

        startLoadingLightning(states.value.selectedDate, states.value.selectedDateFullDay)
    }

    private val onActionListener = object : OnActionListener {
        override fun onLongPress(x: Double, y: Double) = Unit

        override fun onPress() {
            onPressRemovePath()
        }

        override fun onTap(x: Double, y: Double) = Unit

        override fun onTouchDown() = Unit
    }

    override val levelCount = 18
    override val fullWidthOrHeight = 256 * 2.0.pow(levelCount).toInt()

    private val projectComposer: ComposeProjectHolder
    private val probeComposer: ComposeProbeHolder
    private val thunderDetectionActivityComposer: ComposeThunderDetectionActivityHolder
    private val selectedPointComposer: ComposeSelectedPositionHolder
    private val aeroguardPointComposer: ComposeAeroguardPositionHolder

    private val gpsLocation = GpsLocation()

    private val boundingBox = MutableSharedFlow<Pair<LatLng, LatLng>>(1)
    private var lastBoundingBox = LatLng(0.0, 0.0) to LatLng(0.0, 0.0)

    @OptIn(ExperimentalClusteringApi::class)
    override val mapState = MapState(
        levelCount,
        fullWidthOrHeight,
        fullWidthOrHeight,
        tileSize = 256,
        onActionListener = onActionListener
    ).apply {
        setMagnifyingFactor(0)

        maxScale = MaxZoom
        scale = DefaultZoom
        addLayer(tileStreamProvider)

        enableRotation()

        addClusterer("default") { ids ->
            { projectComposer.cluster(size = ids.size) }
        }

        addClusterer(
            "activity",
            clusteringThreshold = 15.dp
        ) { ids ->
            { thunderDetectionActivityComposer.cluster(size = ids.size) }
        }
    }

    override fun saveKeraunicReport(
        location: LatLng,
        from: DateTimeTz,
        to: DateTimeTz,
        project: Project?,
        onDone: () -> Unit
    ) {
        saveKeraunicPdf(location, from, to, project, onDone)
    }

    private val lightningActivityHolder: LightningActivityHolder =
        LightningActivityHolder(viewModelScope)

    init {
        println("CREATING NEW MainScreenModel")

        projectComposer = ComposeProjectHolder(
            mapState = mapState,
            flushCallouts = ::flushCallouts
        )

        probeComposer = ComposeProbeHolder(
            mapState = mapState,
            flushCallouts = ::flushCallouts
        )

        thunderDetectionActivityComposer = ComposeThunderDetectionActivityHolder(
            mapState = mapState,
            flushCallouts = ::flushCallouts
        )

        selectedPointComposer = ComposeSelectedPositionHolder(
            mapState = mapState,
            flushCallouts = ::flushCallouts
        )

        aeroguardPointComposer = ComposeAeroguardPositionHolder(
            mapState = mapState,
            flushCallouts = ::flushCallouts
        )

        safeLaunch {
            val specificData = appModel.getSpecificData()

            val latLng = specificData?.coordinates ?: LatLng(0.0, 0.0)
            val xy = MapUtils.latLngToXY(latLng)

            val zoom = specificData?.zoom ?: MinZoom

            googleApi.initialize()

            updateState {
                copy(
                    currentCoordinates = latLng,
                    currentZoom = zoom,
                    initialized = true
                )
            }

            loadData()

            startLoadingLightning()

            setZoom(zoom)
            mapState.snapScrollTo(
                xy.x,
                xy.y
            )

            safeLaunch {
                clock.start(2.kotlinMinutes).collect {
                    // TODO check depending on the current interval -> "live" vs non live
                    startLoadingLightning()
                }
            }

            safeLaunch {
                mapState.referentialSnapshotFlow().debounce(1.seconds).collect { ref ->
                    updateState { copy(currentZoom = ref.scale) }

                    states.value.currentCoordinates?.let { writeSpecificData(it, ref.scale) }
                }
            }

            safeLaunch {
                mapState.centroidSnapshotFlow().debounce(1.seconds).collect {
                    val coords = MapUtils.xyToLatLng(it.x, it.y)

                    updateState { copy(currentCoordinates = coords) }

                    if (coords.latitude == 0.0 || coords.longitude == 0.0) return@collect

                    writeSpecificData(coords, states.value.currentZoom ?: MinZoom)

                    mapState.visibleBoundingBox().let { box ->
                        MapUtils.xyToLatLng(box.xLeft, box.yTop) to
                                MapUtils.xyToLatLng(box.xRight, box.yBottom)
                    }.let {
                        lastBoundingBox = it
                        boundingBox.emit(it)
                    }
                }
            }

            safeLaunch {
                boundingBox.collect {
                    lightningActivityHolder.transaction {
                        lightningActivityHolder.setBoundingBox(it.first, it.second)
                        updateKnownLightningActivity()

                        updateState {
                            copy(
                                visibleLightningActivity = lightningActivityHolder.state.visible.size,
                            )
                        }
                    }
                }
            }
        }

        mapState.onLongPress { x, y -> selectOnMap(x, y) }
    }

    private fun writeSpecificData(
        coords: LatLng,
        zoom: Double
    ) {
        appModel.writeSpecificData(
            SpecificData(
                zoom = zoom,
                coordinates = coords
            )
        )
    }

    override fun onCleared() {
        println("CLEARED MainScreenModel")

        super.onCleared()
    }

    override var lightningDrawerState: DrawerState? = null
    override var scope: CoroutineScope? = null

    override fun configureMapState() {
        mapState.enableZooming()
        mapState.enableScrolling()
        mapState.enableFlingZoom()
    }

    override fun zoomIn() = applyZoom(1.1)

    // 0.81 = 0.9 * 0.9: each click zooms out twice as much as the original 10% step
    override fun zoomOut() = applyZoom(ZOOM_OUT_COEF)

    private companion object {
        const val ZOOM_OUT_COEF = 0.81
    }

    private fun applyZoom(coef: Double) {
        // make sure that we are both 3.0f > scale > 0.05f
        val newZoom = mapState.scale * coef
        setZoom(max(min(newZoom, MaxZoom), MinZoom))
    }

    private fun setZoom(zoom: Double) {
        mapState.scale = zoom

        safeLaunch {
            updateState { copy(currentZoom = zoom) }

            states.value.currentCoordinates!!.let {
                writeSpecificData(it, zoom)

                val coords = MapUtils.latLngToXY(it.latitude, it.longitude)
                mapState.snapScrollTo(coords.x, coords.y)
            }
        }
    }

    private fun loadData() = safeLaunch(
        onError = {
            it.printStackTrace()
        }
    ) {
        updateState { copy(updatingCoordinates = true) }
    }

    override fun moveTo(location: GeocodeLatLng) {
        safeLaunch {
            val latLng = LatLng(location.latitude, location.longitude)
            val xy = MapUtils.latLngToXY(latLng)

            updateState { copy(initialized = true) }

            mapState.scale = MaxZoom
            mapState.snapScrollTo(
                xy.x,
                xy.y
            )
        }
    }

    override fun moveToOrigin() {
        mapState.scale = DefaultZoom

        safeLaunch {
            println("moveToOrigin")
            try {
                when (val loc = gpsLocation.current()) {
                    is GpsResult.GeolocationFailed -> {
                        println("failed!")
                    }

                    is GpsResult.NotFound -> {
                        println("failed!")
                    }

                    is GpsResult.NotSupported -> {
                        println("failed!")
                    }

                    is GpsResult.PermissionDenied -> {
                        println("failed!")
                    }

                    is GpsResult.Success -> {
                        val latLng =
                            loc.location.coordinates.let { LatLng(it.latitude, it.longitude) }
                        val xy = MapUtils.latLngToXY(latLng)

                        updateState { copy(initialized = true) }

                        mapState.snapScrollTo(
                            xy.x,
                            xy.y
                        )
                    }
                }
            } catch (err: Throwable) {
                println("err loading coordinates ${err.message}")
            }
        }
    }

    override fun lookUp(address: String) {
        safeLaunch {
            try {
                sessionLookUpId++
                val currentId = sessionLookUpId

                _lookUpState.tryEmit(
                    MapActionLookUpState(
                        lookingUp = true,
                        address = address,
                    )
                )

                val results = currentInterface.geocoding(address)

                if (currentId != sessionLookUpId) {
                    // skipped
                    return@safeLaunch
                }

                _lookUpState.tryEmit(
                    MapActionLookUpState(
                        lookingUp = false,
                        address = address,
                        results = results,
                    )
                )
            } catch (err: Exception) {
                err.printStackTrace()

                _lookUpState.tryEmit(
                    MapActionLookUpState(
                        lookingUp = false,
                        address = address,
                        results = emptyList()
                    )
                )
            }
        }
    }

    override fun closeLightningDrawer() = openOrCloseDrawer(false)

    override fun openLightningDrawer() = openOrCloseDrawer(true)

    override fun setFilterAeroguard(show: Boolean) {
        updateState {
            copy(aeroguardLocationsShow = show)
        }

        manageProbeShown(show, states.value.probes)
        manageAeroguardLocations(show, states.value.aeroguardLocations)
    }

    private fun manageProbeShown(showAeroguard: Boolean, probes: List<Probe>) {
        val list = if (showAeroguard) {
            probes
        } else {
            probes.filter { !it.isVirtual }
        }

        states.value.probes.forEach {
            mapState.removePath(it.id())
        }

        list.forEach { addMarker(it) }
    }

    private fun manageAeroguardLocations(
        show: Boolean,
        aeroguardLocations: List<AeroguardLocation>? = null
    ) {
        states.value.aeroguardLocations?.forEach {
            mapState.removeMarker(it.uuid)
        }

        if (!show) return

        aeroguardLocations?.forEach {
            val xy = MapUtils.latLngToXY(it.position)

            mapState.addMarker(
                it.uuid,
                xy.x,
                xy.y,
                c = aeroguardPointComposer.compose(states.value, it, xy) {
                    // nothing
                }
            )
        }
    }

    override fun setSelectedDate(date: DateTimeTz) {
        updateState {
            copy(selectedDate = date.endOfDay, selectedDateRangeStart = null)
        }

        startLoadingLightning(date, states.value.selectedDateFullDay)
    }

    override fun setSelectedDateRange(from: DateTimeTz, to: DateTimeTz) {
        updateState {
            copy(selectedDateRangeStart = from.startOfDay, selectedDate = to.endOfDay)
        }

        startLoadingLightning(to, true)
    }

    override fun setSelectedFullDay(fullDay: Boolean) {
        updateState { copy(selectedDateFullDay = fullDay) }

        startLoadingLightning(states.value.selectedDate, fullDay)
    }

    override fun previousSelectedDate() {
        changeSelectedDate(states.value.selectedDate - 30.minutes)
    }

    override fun previousSelectedDateDay() {
        changeSelectedDate((states.value.selectedDate.startOfDay - 12.hours).endOfDay)
    }

    override fun nextSelectedDate() {
        changeSelectedDate(states.value.selectedDate + 30.minutes)
    }

    override fun nextSelectedDateDay() {
        changeSelectedDate((states.value.selectedDate.endOfDay + 12.hours).endOfDay)
    }

    private fun changeSelectedDate(date: DateTimeTz) {
        date.let {
            launch {
                updateState {
                    copy(selectedDate = it)
                }
            }

            startLoadingLightning(it, states.value.selectedDateFullDay)
        }
    }

    private fun openOrCloseDrawer(isOpen: Boolean) {
        safeLaunch {
            scope?.let {
                withContext(it.coroutineContext) {
                    if (isOpen) {
                        lightningDrawerState?.open()
                    } else {
                        lightningDrawerState?.close()
                    }
                }
            }
        }
    }

    private fun startLoadingLightning() {
        startLoadingLightning(states.value.selectedDate, states.value.selectedDateFullDay)
    }

    private val loadStrikeRadarData = LoadStrikeRadarData(currentInterface)

    private fun savePdf(
        coordinates: LatLng,
        project: Project?,
        instance: ThunderDetectionActivity
    ) = safeLaunch {
        saveFile("incidence_report", "pdf") {
            if (null != project) {
                currentInterface.downloadIncidenceReport(project, instance)
            } else {
                currentInterface.downloadIncidenceReport(coordinates, instance)
            }
        }
        appModel.recordDocumentHistory(
            DocumentHistoryItem(
                id = "incidence_${instance.recTimestampSeconds}_${project?.uuid.orEmpty()}",
                type = DocumentHistoryType.CERT_LIGHTNING,
                siteName = project?.name.orEmpty(),
                latitude = project?.latitude ?: coordinates.latitude,
                longitude = project?.longitude ?: coordinates.longitude,
                createdAtSecondsUtc = DateTime.now().unixMillisLong / 1000L,
                impactLat = instance.latitude,
                impactLon = instance.longitude,
                impactTimestamp = instance.recTimestampSeconds,
                impactAmplitude = instance.pulsePeakAmp,
                impactPercent = 0.0,
                impactExternalId = instance.externalId.orEmpty(),
            )
        )
    }

    private fun saveKeraunicPdf(
        location: LatLng,
        from: DateTimeTz,
        to: DateTimeTz,
        project: Project?,
        onDone: () -> Unit
    ) = safeLaunch(
        onError = {
            it.printStackTrace()
            onDone()
        }
    ) {
        println(
            "https://lpsmanager.io/keraunic?lang=en&" +
                    "latitude=${location.latitude}&" +
                    "longitude=${location.longitude}&" +
                    "fromSeconds=${from.utc.unixMillisLong / 1000}&" +
                    "toSeconds=${to.utc.unixMillisLong / 1000}"
        )

        saveFile("keraunic_report", "pdf") {
            // we are using the local date but it will be extracted
            // so that the actual's position local date corresponding irrespective to utc will be used
            if (null != project) {
                currentInterface.downloadKeraunicReport(project, from, to)
            } else {
                currentInterface.downloadKeraunicReport(location, from, to)
            }
        }
        appModel.recordDocumentHistory(
            DocumentHistoryItem(
                id = "keraunic_${from.utc.unixMillisLong}_${to.utc.unixMillisLong}_${project?.uuid.orEmpty()}",
                type = DocumentHistoryType.KERAUNIC_REPORT,
                siteName = project?.name.orEmpty(),
                latitude = project?.latitude ?: location.latitude,
                longitude = project?.longitude ?: location.longitude,
                createdAtSecondsUtc = DateTime.now().unixMillisLong / 1000L,
            )
        )
        onDone()
    }

    @Suppress("LongMethod")
    @OptIn(ExperimentalClusteringApi::class)
    private fun startLoadingLightning(date: DateTimeTz, fullDay: Boolean) = safeLaunch {
        async {
            try {
                updateState { copy(loading = true) }
                var actualError: Throwable? = null
                val asynced = viewModelScope.async(Dispatchers.CIO) {
                    try {
                        val rangeStart = states.value.selectedDateRangeStart
                        if (rangeStart != null) {
                            loadStrikeRadarData.startLoadingLightning(
                                date, fullDay,
                                rangeFrom = rangeStart,
                                rangeTo = states.value.selectedDate
                            )
                        } else {
                            loadStrikeRadarData.startLoadingLightning(date, fullDay)
                        }
                    } catch (_: LoadStrikeRadarData.LoadStrikeRadarCanceledException) {
                        null
                    } catch (other: Throwable) {
                        other.printStackTrace()
                        Sentry.captureException(other)
                        actualError = other
                        null
                    }
                }

                val result = asynced.await() ?: if (null != actualError) {
                    throw IllegalStateException("Couldn't load data from the backend")
                } else {
                    return@async
                }

                val probes = result.probes
                val projects = result.projects
                val aeroguardLocations = result.aeroguardLocations
                val lightningActivityList = result.lightningActivity.values.toList()

                manageProbeShown(states.value.aeroguardLocationsShow, probes)

                projects.forEachIndexed { index, it ->
                    val xy = MapUtils.latLngToXY(it.latitude, it.longitude)

                    mapState.addMarker(
                        "project_$index",
                        xy.x,
                        xy.y,
                        renderingStrategy = RenderingStrategy.Clustering("default"),
                        c = projectComposer.compose(states.value, it, xy) {
                            unselectOnMap()

                            updateState { copy(selectedProject = it, showSelectPointHint = true) }
                        }
                    )
                }

                manageAeroguardLocations(states.value.aeroguardLocationsShow, aeroguardLocations)

                lightningActivityHolder.transaction {
                    lightningActivityHolder.setLightningActivity(lightningActivityList)
                    updateKnownLightningActivity()

                    updateState {
                        copy(
                            loading = false,
                            probes = probes,
                            lightningActivity = lightningActivityList,
                            visibleLightningActivity = lightningActivityHolder.state.visible.size,
                            projects = projects,
                            aeroguardLocations = aeroguardLocations
                        )
                    }
                }
            } catch (err: Throwable) {
                Sentry.captureException(err)

                appModel.showToast(
                    title = getString(Res.string.error_message_title),
                    action = getString(Res.string.dismiss),
                    type = ToastType.Error
                )

                updateState { copy(loading = false) }
            }
        }
    }

    private val setOfLightningPointAdded = mutableSetOf<String>()

    @OptIn(ExperimentalClusteringApi::class)
    private fun updateKnownLightningActivity() {
        val visible = lightningActivityHolder.state.visible
        val toRemoveFromVisibleScreen = lightningActivityHolder.state
            .run { toRemoveFromVisibleScreen.values + removed }

        lightningActivityHolder.commitState()

        toRemoveFromVisibleScreen.forEach { activity ->
            setOfLightningPointAdded.removeAll { it == activity.externalId }
            mapState.removeMarker(activity.externalId)
        }

        visible.values.forEach { activity ->
            if (setOfLightningPointAdded.contains(activity.externalId)) {
                return@forEach
            }

            setOfLightningPointAdded.add(activity.externalId)

            val xy = MapUtils.latLngToXY(activity.coordinates)

            mapState.addMarker(
                activity.externalId,
                xy.x,
                xy.y,
                renderingStrategy = RenderingStrategy.Clustering("activity"),
                c = thunderDetectionActivityComposer.compose(states.value, activity, xy) {
                    flushCallouts()
                    val poiPosition = states.value.selectedProject?.coordinates
                        ?: states.value.selectedPoint

                    mapState.addCallout(activity.externalId + "_callout", xy.x, xy.y) {
                        ShowThunderActivityInfo(
                            Modifier.width(260.dp).padding(bottom = 10.dp),
                            activity,
                            poiPosition,
                            states.value.selectedProject,
                        )
                    }

                    poiPosition?.let {
                        showPathLocation(listOf(it, activity.coordinates))
                    }
                }
            )
        }

        // just in case...
        visible.forEach { (key, value) ->
            if (!setOfLightningPointAdded.contains(value.externalId)) {
                setOfLightningPointAdded.add(value.externalId)
            }
        }
    }

    private fun flushCallouts() {
        mapState.onPress()
    }

    private fun findRadiusMeterWithDelta(
        project: Project,
        activity: ThunderDetectionActivity,
        expectedProbability: Double
    ) = findRadiusMeterWithDelta(project.coordinates, activity, expectedProbability)

    private fun findRadiusMeterWithDelta(
        position: LatLng,
        activity: ThunderDetectionActivity,
        expectedProbability: Double
    ): Int {
        var distanceMeter = 0
        var probability = 0.0
        do {
            distanceMeter += 1

            val bearing = MapUtils.calculateBearing(activity.coordinates, position)
            val otherPosition = MapUtils.delta(activity.coordinates, distanceMeter, bearing)
            probability = testMahalanobisKMP(
                position1 = activity.coordinates,
                position2 = otherPosition,
                a = activity.ellipseSemiMinorMeter.toDouble(),
                b = activity.ellipseSemiMajorMeter.toDouble(),
                thetaDeg = activity.ellipseAngleDegree.toDouble()
            ).preciseProbability

            if (probability < expectedProbability) distanceMeter -= 1
        } while (probability > expectedProbability)

        return distanceMeter
    }

    private fun addCircle(
        id: String,
        position: LatLng,
        radiusMeter: Int,
        color: Color = Color.Transparent
    ) {
        mapState.addPath(
            id,
            width = 2.dp,
            color = color,
            fillColor = AppColor.OrangeSemiTransparent,
        ) {
            MapUtils.circle(position, radiusMeter) {
                MapUtils.latLngToXY(this).toPair()
            }?.let { list -> this.addPoints(list) }
        }
    }

    private fun unselectOnMap() = safeLaunch {
        mapState.removeMarker("selectedPoint")

        updateState { copy(selectedPoint = null) }
    }

    private fun selectOnMap(x: Double, y: Double) = safeLaunch {
        val selectedPoint = MapUtils.xyToLatLng(x, y)
        mapState.removeMarker("selectedPoint")

        mapState.addMarker(
            "selectedPoint",
            x,
            y,
            c = selectedPointComposer.compose(
                states.value,
                model = selectedPoint,
                XY(x, y)
            ) {
                // we don't want this for now since we are doing the ng computation unselectOnMap()
            }
        )

        updateState {
            copy(
                selectedProject = null,
                selectedPoint = selectedPoint
            )
        }
    }

    @OptIn(ExperimentalClusteringApi::class)
    private fun addMarker(probe: Probe) {
        val xy = MapUtils.latLngToXY(probe.coordinates())

        mapState.addMarker(
            "probe_center_${probe.id()}",
            xy.x,
            xy.y,
            c = probeComposer.compose(states.value, probe, xy) {
                val newList = states.value.selectedProbes.let { list ->
                    if (list.any { it.id() == probe.id() }) {
                        list.filter { it.id() != probe.id() }
                    } else {
                        list + probe
                    }
                }

                println("now having ${newList.size} probes in the list !")
                safeLaunch {
                    commitGroupingProbes(newList)
                }

                updateState {
                    copy(
                        selectedProbes = newList,
                        startGroupingCircles = newList.isNotEmpty()
                    )
                }
            }
        )

        addCircle(
            probe.id(),
            LatLng(probe.latitude, probe.longitude),
            probe.radiusKm * 1000,
            color = when {
                probe.isVirtual -> AppColor.Blue
                probe.isMain -> AppColor.RedDarker
                else -> AppColor.Orange
            }
        )
    }

    private val lineBetweenProjectAndLightning = "lineBetweenProjectAndLightning"

    private val addedPath = mutableListOf<String>()

    private fun showPathLocation(path: List<LatLng>) {
        if (path.isEmpty()) return

        mapState.removePath(lineBetweenProjectAndLightning)
        mapState.addPath(
            lineBetweenProjectAndLightning,
            width = 4.dp,
            color = AppColor.RedDarker
        ) {
            this.addPoints(
                path.map { latLng ->
                    val xy = MapUtils.latLngToXY(latLng.latitude, latLng.longitude)
                    xy.x to xy.y
                }
            )
        }
    }

    private fun onPressRemovePath() {
        mapState.removePath(lineBetweenProjectAndLightning)
        addedPath.forEach { mapState.removePath(it) }
        addedPath.clear()
    }

    override fun stopGroupingProbes() {
        mapState.removePath("virtual_pending")

        updateState {
            copy(
                startGroupingCircles = false,
                selectedProbes = emptyList()
            )
        }
    }

    fun commitGroupingProbes(probes: List<Probe>) {
        val points = probes.map { it.toCircle() }
        val center = MapUtils.findFeasibleCenter(points, 100.0)

        val areAnyVirtual = probes.any { it.isVirtual }
        if (areAnyVirtual) {
            println("WARNING : SOME PROBES ARE VIRTUAL, PLEASE CHECK YOUR SELECTION")
        }

        mapState.removePath("virtual_pending")
        if (null == center) return

        val text =
            "Please make the following, create a Virtual probe at lat=${center.latitude}, long=${center.longitude}\n" +
                    "and make all circle ${probes.map { it.id() }} to depend on it"

        println(text)

        addCircle(
            "virtual_pending",
            LatLng(center.latitude, center.longitude),
            100 * 1000,
            color = AppColor.Blue
        )
    }
}

@Suppress("TooManyFunctions")
class FakeMainScreenModel(
    private val updating: Boolean = false,
) : MainScreenModel {
    override fun configureMapState() = Unit

    override fun zoomIn() = Unit

    override fun zoomOut() = Unit

    override fun moveToOrigin() = Unit

    override val lookUpState: StateFlow<MapActionLookUpState> =
        MutableStateFlow(MapActionLookUpState())

    override fun lookUp(address: String) = Unit

    override fun moveTo(location: GeocodeLatLng) = Unit

    override fun closeLightningDrawer() = Unit

    override fun openLightningDrawer() = Unit

    override fun setFilterAeroguard(show: Boolean) = Unit

    override fun setSelectedDate(date: DateTimeTz) = Unit

    override fun setSelectedDateRange(from: DateTimeTz, to: DateTimeTz) = Unit

    override fun setSelectedFullDay(fullDay: Boolean) = Unit

    override fun nextSelectedDate() = Unit

    override fun previousSelectedDateDay() = Unit

    override fun nextSelectedDateDay() = Unit

    override fun previousSelectedDate() = Unit

    override val levelCount: Int = 8
    override val fullWidthOrHeight: Int = 6
    override val mapState: MapState
        get() = MapState(levelCount, fullWidthOrHeight, fullWidthOrHeight)

    override fun saveKeraunicReport(
        location: LatLng,
        from: DateTimeTz,
        to: DateTimeTz,
        project: Project?,
        onDone: () -> Unit
    ) = Unit

    override var lightningDrawerState: DrawerState? = null
    override var scope: CoroutineScope? = null

    override val states: StateFlow<MainScreenModelState>
        get() = MutableStateFlow(
            MainScreenModelState(
                updatingCoordinates = updating,
                selectedDate = DateTime.now().local
            )
        )

    override fun setState(state: MainScreenModelState) = Unit

    override fun updateState(block: MainScreenModelState.() -> MainScreenModelState) = Unit

    override fun stopGroupingProbes() = Unit
}
